import { createFileRoute, useParams } from "@tanstack/react-router";
import { trpc } from "@/hooks/trpc";
import type {
  EquipmentModelId,
  FermentationProcedureId,
  FermentationProcedureTaskId,
} from "@brewbase/db/types";
import { FermentationStatusEnum } from "@brewbase/db/types";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { toast } from "sonner";
import { useMemo, useState, useCallback, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SlimTabs,
  SlimTabsList,
  SlimTabsTrigger,
} from "@/components/ui/slim-tabs";
import {
  GripVerticalIcon,
  PlusIcon,
  XIcon,
  ClipboardListIcon,
  CheckCircle2Icon,
  CircleDotIcon,
  ClockIcon,
  PlayIcon,
  ArchiveIcon,
  FlagIcon,
  ListIcon,
} from "lucide-react";
import InlineEditableInput from "@/components/InlineEditableInput";

export const Route = createFileRoute(
  "/fermentations/procedures/$procedure_id/tasks",
)({
  component: TasksPage,
});

// Status configuration
const STATUS_CONFIG: Record<
  FermentationStatusEnum,
  { label: string; icon: React.ReactNode }
> = {
  [FermentationStatusEnum.PLANNED]: {
    label: "Planned",
    icon: <ClockIcon className="h-3.5 w-3.5" />,
  },
  [FermentationStatusEnum.PREPARING]: {
    label: "Preparing",
    icon: <CircleDotIcon className="h-3.5 w-3.5" />,
  },
  [FermentationStatusEnum.PREPARED]: {
    label: "Prepared",
    icon: <CheckCircle2Icon className="h-3.5 w-3.5" />,
  },
  [FermentationStatusEnum.RUNNING]: {
    label: "Running",
    icon: <PlayIcon className="h-3.5 w-3.5" />,
  },
  [FermentationStatusEnum.ENDED]: {
    label: "Ended",
    icon: <FlagIcon className="h-3.5 w-3.5" />,
  },
  [FermentationStatusEnum.ARCHIVED]: {
    label: "Archived",
    icon: <ArchiveIcon className="h-3.5 w-3.5" />,
  },
};

// Lifecycle order for status phases
const STATUS_ORDER: FermentationStatusEnum[] = [
  FermentationStatusEnum.PLANNED,
  FermentationStatusEnum.PREPARING,
  FermentationStatusEnum.PREPARED,
  FermentationStatusEnum.RUNNING,
  FermentationStatusEnum.ENDED,
];

// Types for our task items
interface ProcedureTask {
  id: FermentationProcedureTaskId;
  title: string;
  instructions: string | null;
  target_status: FermentationStatusEnum | null;
  is_optional: boolean;
  display_order: number;
  calibration_target_key: string | null;
}

/** The targets a procedure's tasks may refer to: those of its fermentor model. */
interface CalibrationTargetOption {
  key: string;
  label: string;
}

function TasksPage() {
  const { procedure_id } = useParams({ from: Route.id });
  const procedureId = procedure_id as unknown as FermentationProcedureId;
  const utils = trpc.useUtils();

  // State
  const [selectedId, setSelectedId] =
    useState<FermentationProcedureTaskId | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("ALL");

  // Fetch procedure data
  const { data: procedure, isLoading: isLoadingProcedure } =
    trpc.fermentationProcedures.getWithDetails.useQuery(procedureId);

  // Calibration targets of the procedure's fermentor model (none without a model)
  const fermentorModelId = (procedure?.fermentor_model_id ?? null) as EquipmentModelId | null;
  const calibrationTargets = trpc.fermentationCalibration.targets.useQuery(
    { equipmentModelId: fermentorModelId ?? (0 as EquipmentModelId) },
    { enabled: fermentorModelId !== null },
  );
  const targetOptions: CalibrationTargetOption[] | null =
    fermentorModelId === null
      ? null
      : (calibrationTargets.data?.targets ?? []).map((t) => ({ key: t.key, label: t.label }));

  // Mutations
  const addTaskMutation = trpc.fermentationProcedures.addTask.useMutation({
    onSuccess: (data) => {
      toast.success("Task added");
      utils.fermentationProcedures.getWithDetails.invalidate(procedureId);
      setSelectedId(data.id);
    },
    onError: (error) => {
      toast.error(`Failed to add task: ${error.message}`);
    },
  });

  const updateTaskMutation = trpc.fermentationProcedures.updateTask.useMutation(
    {
      onSuccess: () => {
        utils.fermentationProcedures.getWithDetails.invalidate(procedureId);
      },
      onError: (error) => {
        toast.error(`Failed to update task: ${error.message}`);
      },
    },
  );

  const removeTaskMutation = trpc.fermentationProcedures.removeTask.useMutation(
    {
      onSuccess: () => {
        toast.success("Task removed");
        utils.fermentationProcedures.getWithDetails.invalidate(procedureId);
        setSelectedId(null);
      },
      onError: (error) => {
        toast.error(`Failed to remove task: ${error.message}`);
      },
    },
  );

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Get sorted tasks
  const tasks: ProcedureTask[] = useMemo(() => {
    if (!procedure?.tasks) return [];
    return [...procedure.tasks].sort((a, b) => {
      const orderDiff = (a.display_order ?? 0) - (b.display_order ?? 0);
      if (orderDiff !== 0) return orderDiff;
      return (a.id as number) - (b.id as number);
    });
  }, [procedure?.tasks]);

  // Filtered tasks
  const filteredTasks = useMemo(() => {
    if (filterStatus === "ALL") return tasks;
    return tasks.filter((t) => t.target_status === filterStatus);
  }, [tasks, filterStatus]);

  // Selected task
  const selectedTask = useMemo(() => {
    if (!selectedId) return null;
    return tasks.find((t) => t.id === selectedId) ?? null;
  }, [tasks, selectedId]);

  // Initialize selection when tasks load
  useEffect(() => {
    if (!selectedId && filteredTasks.length > 0) {
      setSelectedId(filteredTasks[0].id);
    }
  }, [filteredTasks, selectedId]);

  // Count tasks by status
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { ALL: tasks.length };
    STATUS_ORDER.forEach((status) => {
      counts[status] = tasks.filter((t) => t.target_status === status).length;
    });
    return counts;
  }, [tasks]);

  // Handlers
  const handleDragEnd = (event: DragEndEvent) => {
    if (procedure?.is_locked) return;

    const { active, over } = event;
    if (over && active.id !== over.id) {
      const activeId = parseInt(
        active.id as string,
      ) as unknown as FermentationProcedureTaskId;
      const overId = parseInt(
        over.id as string,
      ) as unknown as FermentationProcedureTaskId;

      const oldIndex = filteredTasks.findIndex((t) => t.id === activeId);
      const newIndex = filteredTasks.findIndex((t) => t.id === overId);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Calculate new display orders
        const newOrder = [...filteredTasks];
        const [moved] = newOrder.splice(oldIndex, 1);
        newOrder.splice(newIndex, 0, moved);

        // Update all display orders
        newOrder.forEach((task, idx) => {
          if (task.display_order !== idx) {
            updateTaskMutation.mutate({
              procedureId,
              taskId: task.id,
              displayOrder: idx,
            });
          }
        });
      }
    }
  };

  const handleAddTask = (targetStatus?: FermentationStatusEnum) => {
    addTaskMutation.mutate({
      procedureId,
      title: "New Task",
      targetStatus: targetStatus ?? null,
    });
  };

  const handleUpdateTask = useCallback(
    (updates: Record<string, unknown>) => {
      if (!selectedId || procedure?.is_locked) return;

      updateTaskMutation.mutate({
        procedureId,
        taskId: selectedId,
        ...updates,
      });
    },
    [selectedId, procedureId, procedure?.is_locked, updateTaskMutation],
  );

  const handleRemoveTask = (taskId: FermentationProcedureTaskId) => {
    if (procedure?.is_locked) return;

    removeTaskMutation.mutate({
      procedureId,
      taskId,
    });
  };

  if (isLoadingProcedure) {
    return (
      <div className="p-4 space-y-3 h-full">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!procedure) {
    return <div className="p-4 text-sm">Procedure not found</div>;
  }

  const isLocked = procedure.is_locked;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2 border-b flex-shrink-0">
        <div className="text-sm font-medium">Tasks</div>
        {!isLocked && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleAddTask()}
            disabled={addTaskMutation.isPending}
            className="h-7 text-xs"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Add
          </Button>
        )}
      </div>

      {/* Status Filter Tabs */}
      <div className="flex-shrink-0">
        <SlimTabs value={filterStatus} onValueChange={setFilterStatus}>
          <SlimTabsList className="px-2">
            <SlimTabsTrigger
              value="ALL"
              icon={<ListIcon className="h-3.5 w-3.5" />}
            >
              All ({statusCounts["ALL"]})
            </SlimTabsTrigger>
            {STATUS_ORDER.map((status) => {
              const config = STATUS_CONFIG[status];
              const count = statusCounts[status];
              return (
                <SlimTabsTrigger key={status} value={status} icon={config.icon}>
                  {config.label} ({count})
                </SlimTabsTrigger>
              );
            })}
          </SlimTabsList>
        </SlimTabs>
      </div>

      {tasks.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center text-muted-foreground">
            <p className="text-sm mb-3">No tasks defined.</p>
            {!isLocked && (
              <Button
                onClick={() => handleAddTask()}
                variant="outline"
                size="sm"
                disabled={addTaskMutation.isPending}
              >
                <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
                Add Task
              </Button>
            )}
          </div>
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center text-muted-foreground">
            <p className="text-sm mb-3">
              No tasks for{" "}
              {STATUS_CONFIG[filterStatus as FermentationStatusEnum]?.label ??
                filterStatus}{" "}
              phase.
            </p>
            {!isLocked && filterStatus !== "ALL" && (
              <Button
                onClick={() =>
                  handleAddTask(filterStatus as FermentationStatusEnum)
                }
                variant="outline"
                size="sm"
                disabled={addTaskMutation.isPending}
              >
                <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
                Add Task
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-12">
          {/* Left: List */}
          <div className="md:col-span-5 min-h-0 border-r">
            <ScrollArea className="h-full">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredTasks.map((t) => t.id.toString())}
                  strategy={verticalListSortingStrategy}
                >
                  {/* Table Header */}
                  <div className="grid grid-cols-[24px_1fr_auto_28px] gap-1 px-2 py-1.5 text-xs font-medium text-muted-foreground border-b bg-muted/30 sticky top-0">
                    <div></div>
                    <div>Task</div>
                    <div className="text-center">Phase</div>
                    <div></div>
                  </div>
                  {/* Table Body */}
                  <div>
                    {filteredTasks.map((task) => (
                      <TaskTableRow
                        key={task.id}
                        task={task}
                        selected={selectedId === task.id}
                        onClick={() => setSelectedId(task.id)}
                        onDelete={() => handleRemoveTask(task.id)}
                        readOnly={isLocked}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </ScrollArea>
          </div>

          {/* Right: Inspector */}
          <div className="md:col-span-7 min-h-0">
            {selectedTask ? (
              <TaskInspector
                key={selectedTask.id}
                task={selectedTask}
                onUpdate={handleUpdateTask}
                isLocked={isLocked}
                calibrationTargets={targetOptions}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-muted-foreground bg-muted/20">
                Select a task to edit.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ======================== Task Table Row ========================

function TaskTableRow({
  task,
  selected,
  onClick,
  onDelete,
  readOnly,
}: {
  task: ProcedureTask;
  selected: boolean;
  onClick: () => void;
  onDelete: () => void;
  readOnly: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id.toString(), disabled: readOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 100 : "auto",
  } as React.CSSProperties;

  const statusConfig = task.target_status
    ? STATUS_CONFIG[task.target_status]
    : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        "grid grid-cols-[24px_1fr_auto_28px] gap-1 items-center px-2 py-1.5 text-sm hover:bg-muted/50 cursor-pointer border-b border-border/50 " +
        (selected ? "bg-primary/5 border-l-2 border-l-primary" : "")
      }
      onClick={onClick}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground hover:text-foreground flex-shrink-0 p-0.5"
        aria-label="Drag to reorder"
      >
        <GripVerticalIcon className="h-3 w-3" />
      </button>

      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <ClipboardListIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <span className="truncate font-medium">
            {task.title || (
              <span className="text-muted-foreground italic">Untitled</span>
            )}
          </span>
          {task.is_optional && (
            <span className="text-xs bg-muted text-muted-foreground px-1 py-0.5 rounded flex-shrink-0">
              Optional
            </span>
          )}
          {task.calibration_target_key && (
            <span className="text-xs bg-blue-3 text-blue-11 px-1 py-0.5 font-mono flex-shrink-0">
              calibration: {task.calibration_target_key}
            </span>
          )}
        </div>
      </div>

      <div className="text-center">
        {statusConfig ? (
          <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
            {statusConfig.icon}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground/40">—</span>
        )}
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        disabled={readOnly}
        className="h-5 w-5 p-0 hover:text-destructive hover:bg-destructive/10"
      >
        <XIcon className="h-3 w-3" />
      </Button>
    </div>
  );
}

// ======================== Task Inspector ========================

const NO_TARGET = "__none__";

function TaskInspector({
  task,
  onUpdate,
  isLocked,
  calibrationTargets,
}: {
  task: ProcedureTask;
  onUpdate: (updates: Record<string, unknown>) => void;
  isLocked: boolean;
  /** Null when the procedure has no fermentor model; the field is then hidden. */
  calibrationTargets: CalibrationTargetOption[] | null;
}) {
  const currentKey = task.calibration_target_key;
  const knownTarget =
    currentKey !== null && calibrationTargets?.some((t) => t.key === currentKey);
  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        {/* Title */}
        <div>
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Task Title
          </Label>
          <InlineEditableInput
            autoFocus={task.title === "New Task"}
            value={task.title}
            onSave={(value) => onUpdate({ title: value })}
            placeholder="Enter task title"
            inputClassName="text-sm font-medium mt-1"
            disabled={isLocked}
          />
        </div>

        {/* Target Status */}
        <div>
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Lifecycle Phase
          </Label>
          <p className="text-xs text-muted-foreground mb-2">
            Which phase does this task apply to?
          </p>
          <Select
            value={task.target_status ?? undefined}
            onValueChange={(value) =>
              onUpdate({
                targetStatus: value as FermentationStatusEnum,
              })
            }
            disabled={isLocked}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select phase (optional)" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_ORDER.map((status) => {
                const config = STATUS_CONFIG[status];
                return (
                  <SelectItem key={status} value={status}>
                    <div className="flex items-center gap-2">
                      {config.icon}
                      <span>{config.label}</span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {task.target_status && !isLocked && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onUpdate({ targetStatus: null })}
              className="text-xs mt-1 h-6 px-2"
            >
              Clear phase
            </Button>
          )}
        </div>

        {/* Calibration target */}
        {calibrationTargets !== null && (
          <div>
            <Label className="text-xs uppercase text-muted-foreground font-medium">
              Calibration target
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Completing this task launches the fermentor model's calibration
              target on the tank.
            </p>
            <Select
              value={currentKey ?? NO_TARGET}
              onValueChange={(value) =>
                onUpdate({
                  calibrationTargetKey: value === NO_TARGET ? null : value,
                })
              }
              disabled={isLocked}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TARGET}>None</SelectItem>
                {calibrationTargets.map((t) => (
                  <SelectItem key={t.key} value={t.key}>
                    <span>{t.label}</span>
                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                      {t.key}
                    </span>
                  </SelectItem>
                ))}
                {currentKey !== null && !knownTarget && (
                  <SelectItem value={currentKey}>
                    <span className="font-mono">{currentKey}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      not defined on this model
                    </span>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Divider */}
        <div className="border-t pt-4">
          {/* Optional */}
          <div className="rounded-sm border bg-muted/20 px-3 py-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="is-optional"
                checked={task.is_optional}
                onCheckedChange={(checked) =>
                  onUpdate({ isOptional: !!checked })
                }
                disabled={isLocked}
              />
              <Label htmlFor="is-optional" className="text-sm font-medium">
                Optional Task
              </Label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Optional tasks can be skipped. Required by default.
            </p>
          </div>
        </div>

        {/* Instructions */}
        <div className="border-t pt-4">
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Instructions
          </Label>
          <div className="mt-2">
            <MarkdownEditor
              value={task.instructions ?? ""}
              onChange={(value) => onUpdate({ instructions: value || null })}
              placeholder="Enter detailed instructions for this task..."
              buttons={[
                "bold",
                "italic",
                "bulletList",
                "orderedList",
                "undo",
                "redo",
              ]}
              readOnly={isLocked}
            />
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
